import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Users,
  Clock,
  DollarSign,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

type Tab = 'referrals' | 'pending-earnings' | 'payouts';

export default function Referrals() {
  const [activeTab, setActiveTab] = useState<Tab>('referrals');
  const queryClient = useQueryClient();

  const tabs = [
    { id: 'referrals' as Tab, label: 'Referrals', icon: Users },
    { id: 'pending-earnings' as Tab, label: 'Pending Earnings', icon: Clock },
    { id: 'payouts' as Tab, label: 'Payouts', icon: DollarSign },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Referral Management</h1>
          <p className="text-sm text-zinc-500">Manage referrals, earnings, and payouts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10 pb-px">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              activeTab === tab.id
                ? 'text-white border-b-2 border-white -mb-px'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'referrals' && <ReferralsTab />}
      {activeTab === 'pending-earnings' && <PendingEarningsTab queryClient={queryClient} />}
      {activeTab === 'payouts' && <PayoutsTab queryClient={queryClient} />}
    </div>
  );
}

// ============================================================================
// Referrals Tab (existing functionality)
// ============================================================================

function ReferralsTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referrals', page, statusFilter, levelFilter],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30',
        ...(statusFilter && { status: statusFilter }),
        ...(levelFilter && { level: levelFilter }),
      });
      return apiRequest('GET', `/api/admin/referrals?${params}`);
    },
  });

  return (
    <>
      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="converted">Converted</option>
        </select>
        <select
          value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
        >
          <option value="">All Levels</option>
          <option value="1">Level 1 (Direct)</option>
          <option value="2">Level 2</option>
          <option value="3">Level 3</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Referrer</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Referred</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Level</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Status</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {data?.referrals?.map((ref: any) => (
                  <tr key={ref.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300">
                      {ref.referrer_id.slice(0, 6)}...{ref.referrer_id.slice(-4)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300">
                      {ref.referred_id.slice(0, 6)}...{ref.referred_id.slice(-4)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${
                        ref.level === 1
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : ref.level === 2
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        L{ref.level}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${
                        ref.status === 'converted'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {ref.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {data.referrals?.length || 0} of {data.pagination.total} referrals
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-zinc-400">
                  Page {page} of {data.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ============================================================================
// Pending Earnings Tab
// ============================================================================

function PendingEarningsTab({ queryClient }: { queryClient: any }) {
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Fetch pending earnings
  const { data, isLoading } = useQuery({
    queryKey: ['admin-pending-earnings', page],
    queryFn: () => apiRequest('GET', `/api/admin/earnings/pending?page=${page}&limit=30`),
  });

  // Fetch summary
  const { data: summary } = useQuery({
    queryKey: ['admin-earnings-summary'],
    queryFn: () => apiRequest('GET', '/api/admin/earnings/summary'),
  });

  // Release mutation
  const releaseMutation = useMutation({
    mutationFn: (earningIds: string[]) =>
      apiRequest('POST', '/api/admin/earnings/release', { earning_ids: earningIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-earnings-summary'] });
      setSelectedIds([]);
    },
  });

  const handleSelectAll = () => {
    if (selectedIds.length === data?.earnings?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data?.earnings?.map((e: any) => e.id) || []);
    }
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleRelease = () => {
    if (selectedIds.length > 0) {
      releaseMutation.mutate(selectedIds);
    }
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 border border-white/10 bg-white/5">
          <p className="text-xs text-zinc-500 mb-1">Pending Amount</p>
          <p className="text-xl font-mono text-amber-400">
            ${(summary?.amounts?.pending || 0).toFixed(2)}
          </p>
          <p className="text-xs text-zinc-600">{summary?.counts?.pending || 0} transactions</p>
        </div>
        <div className="p-4 border border-white/10 bg-white/5">
          <p className="text-xs text-zinc-500 mb-1">Claimable Amount</p>
          <p className="text-xl font-mono text-emerald-400">
            ${(summary?.amounts?.claimable || 0).toFixed(2)}
          </p>
          <p className="text-xs text-zinc-600">{summary?.counts?.claimable || 0} transactions</p>
        </div>
        <div className="p-4 border border-white/10 bg-white/5">
          <p className="text-xs text-zinc-500 mb-1">Total Liability</p>
          <p className="text-xl font-mono text-white">
            ${(summary?.liability || 0).toFixed(2)}
          </p>
          <p className="text-xs text-zinc-600">Pending + Claimable</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-400">
          {selectedIds.length} selected
        </p>
        <button
          onClick={handleRelease}
          disabled={selectedIds.length === 0 || releaseMutation.isPending}
          className="px-4 py-2 text-sm bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {releaseMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            `Release Selected (${selectedIds.length})`
          )}
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-xs text-zinc-500 px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === data?.earnings?.length && data?.earnings?.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Referrer</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Type</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Level</th>
                  <th className="text-right text-xs text-zinc-500 px-4 py-3">Amount</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {data?.earnings?.map((earning: any) => (
                  <tr key={earning.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(earning.id)}
                        onChange={() => handleSelect(earning.id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300">
                      {earning.referrer_id.slice(0, 6)}...{earning.referrer_id.slice(-4)}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {earning.earning_type}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 bg-white/10 text-zinc-300">
                        L{earning.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-amber-400">
                      ${parseFloat(earning.earning_amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {new Date(earning.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {data.earnings?.length || 0} of {data.pagination.total} pending earnings
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-zinc-400">
                  Page {page} of {data.pagination.totalPages || 1}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= (data.pagination.totalPages || 1)}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ============================================================================
// Payouts Tab
// ============================================================================

function PayoutsTab({ queryClient }: { queryClient: any }) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [txHash, setTxHash] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-payouts', page, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30',
        ...(statusFilter && { status: statusFilter }),
      });
      return apiRequest('GET', `/api/admin/payouts?${params}`);
    },
  });

  const processMutation = useMutation({
    mutationFn: ({ id, action, tx_hash }: { id: string; action: string; tx_hash?: string }) =>
      apiRequest('PUT', `/api/admin/payouts/${id}`, { action, tx_hash }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payouts'] });
      setSelectedPayout(null);
      setTxHash('');
    },
  });

  const handleProcess = (action: 'approve' | 'reject') => {
    if (selectedPayout) {
      processMutation.mutate({
        id: selectedPayout.id,
        action,
        tx_hash: action === 'approve' ? txHash : undefined,
      });
    }
  };

  return (
    <>
      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">User</th>
                  <th className="text-right text-xs text-zinc-500 px-4 py-3">Amount</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Destination</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Status</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Requested</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.payouts?.map((payout: any) => (
                  <tr key={payout.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300">
                      {payout.user_id.slice(0, 6)}...{payout.user_id.slice(-4)}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-emerald-400">
                      ${parseFloat(payout.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-400">
                      {payout.destination_address?.slice(0, 6)}...{payout.destination_address?.slice(-4)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${
                        payout.status === 'completed'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : payout.status === 'failed'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {new Date(payout.requested_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {payout.status === 'pending' && (
                        <button
                          onClick={() => setSelectedPayout(payout)}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Process
                        </button>
                      )}
                      {payout.tx_hash && (
                        <a
                          href={`https://etherscan.io/tx/${payout.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          View TX
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {data.payouts?.length || 0} of {data.pagination.total} payouts
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-zinc-400">
                  Page {page} of {data.pagination.totalPages || 1}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= (data.pagination.totalPages || 1)}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Process Modal */}
      {selectedPayout && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-white/10 p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-white mb-4">Process Payout</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-zinc-500 mb-1">User</p>
                <p className="text-sm font-mono text-zinc-300">{selectedPayout.user_id}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 mb-1">Amount</p>
                <p className="text-xl font-mono text-emerald-400">
                  ${parseFloat(selectedPayout.amount).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 mb-1">Destination</p>
                <p className="text-sm font-mono text-zinc-300 break-all">
                  {selectedPayout.destination_address}
                </p>
              </div>
              <div>
                <label className="text-xs text-zinc-500 block mb-1">
                  Transaction Hash (for approval)
                </label>
                <input
                  type="text"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="0x..."
                  className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setSelectedPayout(null)}
                className="flex-1 px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => handleProcess('reject')}
                disabled={processMutation.isPending}
                className="flex-1 px-4 py-2 text-sm bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                Reject
              </button>
              <button
                onClick={() => handleProcess('approve')}
                disabled={processMutation.isPending || !txHash}
                className="flex-1 px-4 py-2 text-sm bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {processMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  'Approve'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
